import { useMemo, useState } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dmExcludedTeethKey } from '../../../../../../hooks/useDmExcludedTeeth';
import {
    updateExcludedTeeth,
    type ExcludedTeethMap
} from '../../../../../../services/dmMonitoringControl';
import { DmButton } from '../DmControlPrimitives';
import { DmModalShell, type DmShellLayout } from '../DmModalShell';
import { errorText } from '../dmControlUtils';
import { dmControlMutationKey } from '../useDmControlMutation';
import { useFlash } from '../useFlash';
import { ExcludedTeethNotificationRow } from './ExcludedTeethNotificationRow';
import { ExcludedTeethSidebar, OVERVIEW } from './ExcludedTeethSidebar';
import { notificationEntries, notificationLabel, taxonomyWith } from './excludedTeethTaxonomy';
import { useExcludedTeethDraft } from './useExcludedTeethDraft';

const APPLIED_FLASH_MS = 2200;

interface ExcludedTeethEditorProps {
    layout: DmShellLayout;
    patientId: string;
    patientName: string;
    saved: ExcludedTeethMap;
    onCancel: () => void;
    onSaved: () => void;
    onClose: () => void;
}

// The second step of the DM control modal; Back and Cancel drop unsaved edits.
export function ExcludedTeethEditor({
    layout,
    patientId,
    patientName,
    saved,
    onCancel,
    onSaved,
    onClose
}: ExcludedTeethEditorProps) {
    const queryClient = useQueryClient();
    const { teethFor, setTeeth, changes } = useExcludedTeethDraft(saved);
    const categories = useMemo(() => taxonomyWith(Object.keys(saved)), [saved]);
    const entries = useMemo(() => notificationEntries(categories), [categories]);
    const [selectedId, setSelectedId] = useState(OVERVIEW);
    const [search, setSearch] = useState('');
    const [openReference, setOpenReference] = useState<string | null>(null);
    const [applied, setApplied] = useFlash<{ reference: string; count: number }>(APPLIED_FLASH_MS);
    const save = useMutation({
        mutationKey: dmControlMutationKey(patientId),
        mutationFn: () => updateExcludedTeeth(patientId, changes),
        onSuccess: (map) => {
            queryClient.setQueryData(dmExcludedTeethKey(patientId), map);
            onSaved();
        },
        // DM may have applied the notifications before the one it refused, so reload the map.
        onError: () => {
            void queryClient.invalidateQueries({ queryKey: dmExcludedTeethKey(patientId) });
        }
    });

    const changeCount = Object.keys(changes).length;
    const hasTeeth = (reference: string) => teethFor(reference).length > 0;
    const counts = new Map(
        categories.flatMap((category) =>
            category.observations.map((observation) => [
                observation.id,
                observation.references.filter(hasTeeth).length
            ])
        )
    );
    const query = search.trim().toLowerCase();
    let visible = entries.filter((entry) => entry.observation.id === selectedId);
    let title = visible[0]?.observation.label ?? '';
    let subtitle = visible[0]
        ? `${visible[0].category.label} · ${visible.length} notifications`
        : '';
    if (query) {
        visible = entries.filter((entry) =>
            notificationLabel(entry.reference).toLowerCase().includes(query)
        );
        title = `Results for "${search.trim()}"`;
        subtitle = `${visible.length} notification${visible.length === 1 ? '' : 's'}`;
    } else if (selectedId === OVERVIEW) {
        visible = entries.filter(
            (entry) => hasTeeth(entry.reference) || entry.reference in changes
        );
        title = 'Overall view';
        subtitle = 'Notifications that have excluded teeth, across all categories';
    }
    const showPath = !!query || selectedId === OVERVIEW;

    const footer = (
        <>
            <div className="mr-auto text-xs">
                {save.isError ? (
                    <span className="flex items-center gap-1.5 text-red-500">
                        <ExclamationCircleIcon className="h-4 w-4" />
                        <span>
                            <span className="font-semibold">Dental Monitoring:</span>{' '}
                            {errorText(save.error)}
                        </span>
                    </span>
                ) : (
                    <span className="text-gray-500">
                        {changeCount
                            ? `${changeCount} notification${changeCount === 1 ? '' : 's'} edited`
                            : 'No changes yet'}
                    </span>
                )}
            </div>
            <DmButton onClick={onCancel} disabled={save.isPending}>
                Cancel
            </DmButton>
            <DmButton
                kind="primary"
                disabled={changeCount === 0 || save.isPending}
                loading={save.isPending}
                onClick={() => save.mutate()}
            >
                {save.isPending ? 'Saving…' : 'Save changes'}
            </DmButton>
        </>
    );

    return (
        <DmModalShell
            layout={layout}
            title="Excluded teeth"
            subtitle={`${patientName} · Teeth Dental Monitoring should ignore, per notification`}
            onBack={onCancel}
            onClose={onClose}
            dismissible={changeCount === 0}
            busy={save.isPending}
            footer={footer}
            panes
        >
            <ExcludedTeethSidebar
                categories={categories}
                selectedId={selectedId}
                onSelect={(id) => {
                    setSelectedId(id);
                    setSearch('');
                    setOpenReference(null);
                }}
                search={search}
                onSearch={setSearch}
                counts={counts}
                overviewCount={entries.filter((entry) => hasTeeth(entry.reference)).length}
            />
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="mx-6 shrink-0 border-b border-gray-200 pb-3 pt-4">
                    <h3 className="text-base font-semibold text-gray-800">{title}</h3>
                    <p className="text-xs text-gray-500">{subtitle}</p>
                </div>
                <OverlayScrollArea className="min-h-0 flex-1" contentClassName="px-6 pb-4">
                    {visible.length === 0 ? (
                        <p className="py-10 text-center text-[13px] text-gray-500">
                            {query
                                ? 'No notifications match your search.'
                                : 'No teeth are excluded yet. Pick an observation on the left to start.'}
                        </p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {visible.map((entry) => (
                                <ExcludedTeethNotificationRow
                                    key={entry.reference}
                                    entry={entry}
                                    teeth={teethFor(entry.reference)}
                                    showPath={showPath}
                                    isOpen={openReference === entry.reference}
                                    isEdited={entry.reference in changes}
                                    appliedCount={
                                        applied?.reference === entry.reference
                                            ? applied.count
                                            : null
                                    }
                                    onToggle={() =>
                                        setOpenReference(
                                            openReference === entry.reference
                                                ? null
                                                : entry.reference
                                        )
                                    }
                                    onChange={(teeth) => setTeeth([entry.reference], teeth)}
                                    onApplyToObservation={() => {
                                        setTeeth(
                                            entry.observation.references,
                                            teethFor(entry.reference)
                                        );
                                        setApplied({
                                            reference: entry.reference,
                                            count: entry.observation.references.length
                                        });
                                    }}
                                />
                            ))}
                        </ul>
                    )}
                </OverlayScrollArea>
            </div>
        </DmModalShell>
    );
}
